/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  CodeSnippet,
  FormLabel,
  ListItem,
  OrderedList
} from 'carbon-components-react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { copyContentToClipboard } from '../../../../ia/util/clipboard/clipboard';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { sharedMessages } from '../../messages';
import { AppRegistrationMessages } from '../app-registration/messages';
import { links } from '../links';
import { FIELDS } from '../schema';
import { TeamsAppMessages as messages } from './messages';

export const TeamsAppContent = ({ isQuickSetup }: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();
  const formMethods = useFormContext();
  const { getFieldValue } = useFormHelpers(formMethods);

  return (
    <OrderedList className="TabContent__subtitle TabContent__link-larger-text">
      <ListItem>
        <FormattedMessageWithLink
          url={links.MICROSOFT_TEAMS_APP_LINK}
          {...messages.descriptionOne}
        />
      </ListItem>
      <ListItem>
        <FormattedMessageWithLink {...messages.descriptionTwo} />
        {isQuickSetup && (
          <div>
            <div className="TabContent__spacer8" />
            <FormLabel className="TabContent__field-label">
              <FormattedMessage {...AppRegistrationMessages.appId} />
            </FormLabel>
            <div className="TabContent__spacer8" />
            <CodeSnippet
              id="teams-app-id"
              feedback={t(sharedMessages.copied)}
              copyButtonDescription={t(sharedMessages.copyToClipboard)}
              copyLabel={t(sharedMessages.copyToClipboard)}
              onClick={() =>
                copyContentToClipboard(getFieldValue(FIELDS.APP_ID))
              }>
              {getFieldValue(FIELDS.APP_ID)}
            </CodeSnippet>
          </div>
        )}
      </ListItem>
      <ListItem>
        <FormattedMessage
          values={{
            configure: <strong>{messages.configure.defaultMessage}</strong>,
            appFeatures: <strong>{messages.appFeatures.defaultMessage}</strong>,
            bot: <strong>{messages.bot.defaultMessage}</strong>,
            selectExistingBot: (
              <strong>{messages.selectExistingBot.defaultMessage}</strong>
            ),
            personal: <strong>{messages.personal.defaultMessage}</strong>,
            team: <strong>{messages.team.defaultMessage}</strong>,
            groupChat: <strong>{messages.groupChat.defaultMessage}</strong>,
            save: <strong>{messages.save.defaultMessage}</strong>
          }}
          {...messages.descriptionThree}
        />
      </ListItem>
      <ListItem>
        <FormattedMessage
          values={{
            personal: <strong>{messages.personal.defaultMessage}</strong>,
            team: <strong>{messages.team.defaultMessage}</strong>,
            groupChat: <strong>{messages.groupChat.defaultMessage}</strong>,
            save: <strong>{messages.save.defaultMessage}</strong>
          }}
          {...messages.descriptionFour}
        />
      </ListItem>
      <ListItem>
        <FormattedMessage
          values={{
            publish: <strong>{messages.publish.defaultMessage}</strong>
          }}
          {...messages.descriptionFive}
        />
      </ListItem>
    </OrderedList>
  );
};
